/**
 * Minimal typed HTTP client for the client API (docs/API.md): JSON in/out, CSRF double-submit header on every
 * non-GET request (SEC-09; a missing or rejected `pc_csrf` cookie is fetched once from GET /auth/csrf), uniform
 * ApiError on failures.
 */
import { ApiError, NetworkError } from './api_error';
import type { HttpMethod, HttpTransport, TransportRequest, TransportResponse } from './http_transport';

export const CLIENT_API_PREFIX = '/api/v1';
// Production sets the cookie with the __Host- prefix (center/core/csrf.py); development uses the bare name.
const CSRF_COOKIE_NAMES = ['__Host-pc_csrf', 'pc_csrf'] as const;
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const CSRF_TOKEN_PATH = '/auth/csrf';
const CSRF_FAILED_DETAIL = 'csrf_failed';

export type QueryParameterValue = string | number | boolean | null | undefined | readonly string[];

export interface RequestOptions {
  query?: Record<string, QueryParameterValue>;
  body?: unknown;
  /** Multipart body (file uploads); mutually exclusive with `body`. The browser sets the boundary header. */
  formData?: FormData;
  signal?: AbortSignal | undefined;
}

export function buildQueryString(queryParameters: Record<string, QueryParameterValue> | undefined): string {
  if (queryParameters === undefined) {
    return '';
  }
  const searchParams = new URLSearchParams();
  for (const [parameterName, parameterValue] of Object.entries(queryParameters)) {
    if (parameterValue === null || parameterValue === undefined || parameterValue === '') {
      continue;
    }
    if (Array.isArray(parameterValue)) {
      if (parameterValue.length > 0) {
        searchParams.set(parameterName, parameterValue.join(','));
      }
      continue;
    }
    searchParams.set(parameterName, String(parameterValue));
  }
  const queryText = searchParams.toString();
  return queryText === '' ? '' : `?${queryText}`;
}

export function readCookie(
  cookieName: string,
  cookieHeader: string = typeof document === 'undefined' ? '' : document.cookie,
): string | null {
  for (const cookiePair of cookieHeader.split(';')) {
    const [rawName, ...valueParts] = cookiePair.trim().split('=');
    if (rawName === cookieName) {
      return decodeURIComponent(valueParts.join('='));
    }
  }
  return null;
}

function readCsrfCookie(): string | null {
  for (const cookieName of CSRF_COOKIE_NAMES) {
    const cookieValue = readCookie(cookieName);
    if (cookieValue !== null) {
      return cookieValue;
    }
  }
  return null;
}

function parseErrorBody(responseBody: unknown): { detail: string; message: string | null } {
  if (typeof responseBody === 'object' && responseBody !== null && 'detail' in responseBody) {
    const errorBody = responseBody as { detail: unknown; message?: unknown };
    return {
      detail: typeof errorBody.detail === 'string' ? errorBody.detail : 'error',
      message: typeof errorBody.message === 'string' ? errorBody.message : null,
    };
  }
  return { detail: 'error', message: null };
}

export class ApiClient {
  constructor(
    private readonly transport: HttpTransport,
    private readonly pathPrefix: string = CLIENT_API_PREFIX,
    private readonly extraHeaders: () => Record<string, string> = () => ({}),
  ) {}

  /** The same API with headers added to every request; they are read anew each time. */
  withHeaders(extraHeaders: () => Record<string, string>): ApiClient {
    return new ApiClient(this.transport, this.pathPrefix, extraHeaders);
  }

  async request<ResponseBody>(
    method: HttpMethod,
    path: string,
    requestOptions: RequestOptions = {},
  ): Promise<ResponseBody> {
    let transportResponse = await this.send(method, path, requestOptions, false);
    if (
      transportResponse.status === 403 &&
      parseErrorBody(transportResponse.body).detail === CSRF_FAILED_DETAIL
    ) {
      // The CSRF cookie expired or was never issued (first login): fetch a fresh one and retry once (SEC-09).
      transportResponse = await this.send(method, path, requestOptions, true);
    }
    if (transportResponse.status >= 200 && transportResponse.status < 300) {
      // The response shape is guaranteed by the API contract (docs/API.md / OpenAPI); it is typed at this edge.
      return transportResponse.body as ResponseBody;
    }
    const errorBody = parseErrorBody(transportResponse.body);
    throw new ApiError(transportResponse.status, errorBody.detail, errorBody.message);
  }

  private async send(
    method: HttpMethod,
    path: string,
    requestOptions: RequestOptions,
    refreshCsrfToken: boolean,
  ): Promise<TransportResponse> {
    const headers: Record<string, string> = { Accept: 'application/json', ...this.extraHeaders() };
    if (requestOptions.body !== undefined && requestOptions.formData === undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (method !== 'GET') {
      const csrfToken = refreshCsrfToken ? null : readCsrfCookie();
      const effectiveCsrfToken = csrfToken ?? (await this.issueCsrfToken(requestOptions.signal));
      if (effectiveCsrfToken !== null) {
        headers[CSRF_HEADER_NAME] = effectiveCsrfToken;
      }
    }
    return this.transportOrNetworkError({
      method,
      url: `${this.pathPrefix}${path}${buildQueryString(requestOptions.query)}`,
      headers,
      body:
        requestOptions.formData ??
        (requestOptions.body === undefined ? null : JSON.stringify(requestOptions.body)),
      signal: requestOptions.signal,
    });
  }

  /** GET /auth/csrf sets the `pc_csrf` cookie and returns its value (double-submit token, SEC-09). */
  private async issueCsrfToken(signal: AbortSignal | undefined): Promise<string | null> {
    const csrfResponse = await this.transportOrNetworkError({
      method: 'GET',
      url: `${this.pathPrefix}${CSRF_TOKEN_PATH}`,
      headers: { Accept: 'application/json' },
      body: null,
      signal,
    });
    const csrfBody = csrfResponse.body;
    if (
      csrfResponse.status === 200 &&
      typeof csrfBody === 'object' &&
      csrfBody !== null &&
      'csrf_token' in csrfBody &&
      typeof csrfBody.csrf_token === 'string'
    ) {
      return csrfBody.csrf_token;
    }
    return readCsrfCookie();
  }

  private async transportOrNetworkError(transportRequest: TransportRequest): Promise<TransportResponse> {
    try {
      return await this.transport(transportRequest);
    } catch (transportError) {
      if (transportError instanceof DOMException && transportError.name === 'AbortError') {
        throw transportError;
      }
      throw new NetworkError(transportError);
    }
  }

  get<ResponseBody>(path: string, requestOptions?: RequestOptions): Promise<ResponseBody> {
    return this.request<ResponseBody>('GET', path, requestOptions);
  }

  post<ResponseBody>(path: string, body?: unknown, requestOptions?: RequestOptions): Promise<ResponseBody> {
    return this.request<ResponseBody>('POST', path, { ...requestOptions, body });
  }

  put<ResponseBody>(path: string, body?: unknown, requestOptions?: RequestOptions): Promise<ResponseBody> {
    return this.request<ResponseBody>('PUT', path, { ...requestOptions, body });
  }

  patch<ResponseBody>(path: string, body?: unknown, requestOptions?: RequestOptions): Promise<ResponseBody> {
    return this.request<ResponseBody>('PATCH', path, { ...requestOptions, body });
  }

  postForm<ResponseBody>(
    path: string,
    formData: FormData,
    requestOptions?: RequestOptions,
  ): Promise<ResponseBody> {
    return this.request<ResponseBody>('POST', path, { ...requestOptions, formData });
  }

  delete<ResponseBody>(path: string, requestOptions?: RequestOptions): Promise<ResponseBody> {
    return this.request<ResponseBody>('DELETE', path, requestOptions);
  }
}
